import { Link } from 'react-router';

import { Icon, focusRingOnDark } from '@/design-system';
import { openServices, orderAccessFor, reportStageReached, type Order } from '@/entities/order';
import { OrderStatusPill } from '@/features/inspections/components/OrderStatusPill';
import { OrderSurveyAction } from '@/features/inspections/components/SurveyActionButton';
import { useSessionStore } from '@/shared/api';
import { cn, formatDayTimeShort, useBreakpoint } from '@/shared/lib';
import { ScreenEyebrow } from '@/shared/ui';

import { paths } from '../router/paths';
import { orderLayoutCopy as copy } from './copy';
import type { OrderTab } from './order-tabs';
import { OrderTabs } from './OrderTabs';

export interface OrderHeaderProps {
    /** `null` zolang de keuring laadt of niet te laden was: dan staan er de weg terug en de tabbladen. */
    order: Order | null;
    tabs: readonly OrderTab[];
    /** Rechts: waar het open tabblad zijn stand en knoppen neerzet (`TabHeader`). */
    asideRef: (element: HTMLDivElement | null) => void;
    /** Over de hele breedte: waar het tabblad uitleg en uitkomsten neerzet. */
    notesRef: (element: HTMLDivElement | null) => void;
}

/**
 * De kop van één keuring, boven Overzicht, Rapport en Klantpagina.
 *
 * Links de weg terug naar Keuringen en wat deze keuring is: nummer en diensten,
 * het adres, en de status, de klant, het moment van de opname en de inspecteur.
 * Rechts wat er op het open tabblad speelt, met de knoppen daarvan. Onderaan de
 * tabbladen. Eén donkere kop: de schermen eronder hebben geen eigen kop meer.
 */
export function OrderHeader({ order, tabs, asideRef, notesRef }: OrderHeaderProps) {
    const { isNarrow } = useBreakpoint();
    const access = orderAccessFor(useSessionStore((state) => state.session));

    return (
        <header className="shrink-0 bg-[var(--ink-800)] px-7 pt-1">
            <div className="flex flex-wrap items-end justify-between gap-x-5 gap-y-3">
                <div className="min-w-0 flex-[1_1_320px]">
                    <div className="flex min-w-0 flex-wrap items-center gap-x-4">
                        <Link
                            to={paths.inspections}
                            aria-label={copy.backLabel}
                            className={cn(
                                '-ml-2 inline-flex min-h-[var(--control-h-md)] items-center gap-[6px] px-2 no-underline',
                                'rounded-[var(--radius-control)] font-[family-name:var(--font-body)] text-[13px] leading-[1.25]',
                                'text-[var(--text-on-dark-2)] hover:bg-[var(--ink-hover-on-dark)] hover:text-[var(--text-on-dark)]',
                                focusRingOnDark,
                            )}
                        >
                            <Icon name="back" size="sm" />
                            {copy.back}
                        </Link>
                        <ScreenEyebrow
                            text={
                                order
                                    ? copy.eyebrow(order.number, order.serviceLabel)
                                    : copy.loading
                            }
                        />
                    </div>

                    <h1 className="m-0 font-[family-name:var(--font-display)] text-[22px] leading-[1.2] font-semibold tracking-[-0.02em] text-[var(--text-on-dark)]">
                        {order ? order.property.shortAddress : copy.loading}
                    </h1>

                    {order ? <OrderFacts order={order} /> : null}
                </div>

                {/* De ene handeling van deze keuring, naast de tabbladen en dus op elk
                    tabblad hetzelfde. Niet in de sleuf van het tabblad ernaast: die is
                    van het open scherm, en twee `primary`-knoppen naast elkaar is er één
                    te veel. Op een telefoon valt hij op zijn eigen regel, over de breedte. */}
                {order ? (
                    <div className={cn('mb-1 flex min-w-0', isNarrow && 'w-full')}>
                        <OrderSurveyAction
                            order={order}
                            access={access}
                            size="sm"
                            onDark
                            fullWidth={isNarrow}
                        />
                    </div>
                ) : null}

                <div
                    ref={asideRef}
                    className={cn(
                        'mb-1 flex min-w-0 flex-wrap items-center gap-2 empty:hidden',
                        isNarrow ? 'justify-start' : 'justify-end text-right',
                    )}
                />
            </div>

            <div ref={notesRef} className="mt-3 flex max-w-[780px] flex-col gap-2 empty:hidden" />

            <OrderTabs tabs={tabs} />
        </header>
    );
}

/**
 * Status, klant, moment en inspecteur: wat je van een keuring wilt weten voordat je iets doet.
 *
 * Is de opname ingediend, dan zegt de status alleen hoe ver het rapport is. Een dienst die
 * buiten Smartkeur wordt gemaakt, zoals een energielabel, kan dan nog open staan; de kop zegt
 * het, zodat een afgeronde keuring niet klaar lijkt terwijl het label er nog niet is.
 */
function OrderFacts({ order }: { order: Order }) {
    const open = reportStageReached(order) ? openServices(order) : [];

    return (
        <dl className="m-0 mt-[10px] mb-1 flex flex-wrap items-center gap-x-4 gap-y-2 font-[family-name:var(--font-body)] text-[13px] leading-[1.35]">
            <div className="flex items-center">
                {/* De pil zegt zelf wat hij is; het label is er voor de schermlezer. */}
                <dt className="sr-only">{copy.status}</dt>
                <dd className="m-0">
                    <OrderStatusPill order={order} />
                </dd>
            </div>
            <Fact label={copy.client} value={order.client.name} />
            <Fact
                label={copy.moment}
                value={order.scheduledAt ? formatDayTimeShort(order.scheduledAt) : copy.notPlanned}
            />
            <Fact label={copy.inspector} value={order.assignee?.name ?? copy.noInspector} />
            {open.length > 0 ? (
                <Fact
                    label={copy.openServices}
                    value={open.map((service) => service.name).join(', ')}
                />
            ) : null}
        </dl>
    );
}

function Fact({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex min-w-0 items-baseline gap-[6px]">
            <dt className="text-[var(--text-on-dark-4-safe)]">{label}</dt>
            <dd className="m-0 min-w-0 text-[var(--text-on-dark-2)]">{value}</dd>
        </div>
    );
}
